// Central place for the built-in ("default") category / subcategory images that ship
// inside public/images. These used to be hardcoded in Menu.js and CategoryProducts.js and
// silently took priority over anything the admin uploaded, which made the admin image
// picker look broken. Everything now goes through resolveCategoryImage() below.

export const PLACEHOLDER_IMAGE = '/images/menu/placeholder.svg';

function toText(v) {
	return v == null ? '' : String(v);
}

// Builds one lowercase haystack out of every translation of a category name.
export function nameBlob(item) {
	if (!item) return '';
	const parts = [];
	if (typeof item.name === 'string') parts.push(item.name);
	if (item.name && typeof item.name === 'object') {
		parts.push(item.name.en, item.name.ro, item.name.ar);
	}
	parts.push(item.name_ro);
	return toText(parts.filter(Boolean).join(' ')).trim().toLowerCase();
}

export function isPlaceholderUrl(url) {
	if (!url) return false;
	const s = toText(url).toLowerCase();
	return s.includes('/images/menu/placeholder.svg') || s.endsWith('placeholder.svg');
}

const isDrinksName = (s) => s.includes('drink') || s.includes('beverage') || s.includes('مشر');
const isFoodName = (s) => s.includes('food') || s.includes('mancare') || s.includes('mâncare') || s.includes('طعام');
const isAppetizersName = (s) => s.includes('appet');

const TOP_LEVEL_RULES = [
	{
		image: '/images/menu/top/drinks.webp',
		match: (s) =>
			s.includes('drink') ||
			s.includes('soft drink') ||
			s.includes('cocktail') ||
			s.includes('mocktail') ||
			s.includes('beverage') ||
			s.includes('مشر')
	},
	{
		image: '/images/menu/top/dessert.webp',
		match: (s) => s.includes('dessert') || s.includes('desert') || s.includes('حلوي')
	},
	{
		image: '/images/menu/top/food.webp',
		match: (s) => isFoodName(s)
	}
];

const DRINK_SUB_RULES = [
	{ image: '/images/menu/drinks/cocktails.webp', match: (s) => s.includes('cocktail') || s.includes('كوكت') },
	{ image: '/images/menu/drinks/mocktails.webp', match: (s) => s.includes('mocktail') || s.includes('موكت') },
	{ image: '/images/menu/drinks/coffee-and-more.webp', match: (s) => s.includes('coffee') || s.includes('cafea') || s.includes('قهوة') },
	{ image: '/images/menu/drinks/freak-shakes-and-frappe.webp', match: (s) => s.includes('freak') || s.includes('frappe') || s.includes('frapp') || s.includes('shake') },
	{ image: '/images/menu/drinks/lemonade-and-fresh-juices.webp', match: (s) => s.includes('lemon') || s.includes('fresh') || s.includes('lemonade') || s.includes('freshes') },
	{ image: '/images/menu/drinks/soft-drinks.webp', match: (s) => s.includes('soft drink') || s === 'soft drinks' || s.includes('gaz') || s.includes('suc') }
];

const FOOD_SUB_RULES = [
	{ image: '/images/menu/food/appetizers.webp', match: (s) => isAppetizersName(s) },
	{ image: '/images/menu/food/burgers.webp', match: (s) => s.includes('burger') },
	{ image: '/images/menu/food/breakfast.webp', match: (s) => s.includes('breakfast') || s.includes('mic dejun') || s.includes('dejun') },
	{ image: '/images/menu/food/soups.webp', match: (s) => s.includes('soup') || s.includes('supa') || s.includes('supă') },
	{ image: '/images/menu/food/salads.webp', match: (s) => s.includes('salad') || s.includes('salate') },
	{ image: '/images/menu/food/pasta.webp', match: (s) => s.includes('pasta') },
	{ image: '/images/menu/food/sides.webp', match: (s) => s.includes('side') || s.includes('garnit') },
	{ image: '/images/menu/food/grill.webp', match: (s) => s.includes('grill') || s.includes('gratar') || s.includes('grătar') },
	{ image: '/images/menu/food/international-dishes.webp', match: (s) => s.includes('international') }
];

const APPETIZER_SUB_RULES = [
	{ image: '/images/menu/food/hot-appetizers.webp', match: (s) => s.includes('hot') },
	{ image: '/images/menu/food/cold-appetizers.webp', match: (s) => s.includes('cold') }
];

const DRINK_MENU_PAGE_RULES = [
	{ images: ['/images/menu/drinks-pages/mocktails.jpg'], match: (s) => s.includes('mocktail') },
	{ images: ['/images/menu/drinks-pages/cocktails.jpg'], match: (s) => s.includes('cocktail') },
	{ images: ['/images/menu/drinks-pages/soft-drinks.jpg'], match: (s) => s.includes('soft drink') || s === 'soft drinks' },
	{ images: ['/images/menu/drinks-pages/coffee-and-more.jpg', '/images/menu/drinks-pages/coffee-and-more-2.jpg'], match: (s) => s.includes('coffee') },
	{ images: ['/images/menu/drinks-pages/freak-shakes-and-frappe.jpg'], match: (s) => s.includes('freak') || s.includes('frappe') || s.includes('frapp') || s.includes('shake') },
	{ images: ['/images/menu/drinks-pages/lemonades-and-fresh-juices.jpg'], match: (s) => s.includes('lemon') || s.includes('fresh') || s.includes('lemonade') || s.includes('freshes') }
];

function firstMatch(rules, s, key) {
	if (!s) return null;
	for (const rule of rules) {
		if (rule.match(s)) return rule[key];
	}
	return null;
}

/**
 * The built-in image that ships with the app for this category, or null if there isn't one.
 * `parentCat` is optional but lets subcategory rules resolve exactly like before.
 */
export function getDefaultCategoryImage(cat, parentCat) {
	if (!cat) return null;
	const s = nameBlob(cat);
	if (!s) return null;

	if (!cat.parentId) return firstMatch(TOP_LEVEL_RULES, s, 'image');

	const parent = parentCat ? nameBlob(parentCat) : '';
	if (isAppetizersName(parent)) {
		const hit = firstMatch(APPETIZER_SUB_RULES, s, 'image');
		if (hit) return hit;
	}
	if (isDrinksName(parent)) {
		const hit = firstMatch(DRINK_SUB_RULES, s, 'image');
		if (hit) return hit;
	}
	if (isFoodName(parent)) {
		const hit = firstMatch(FOOD_SUB_RULES, s, 'image');
		if (hit) return hit;
	}
	return null;
}

/** Built-in full-page menu images (currently only the drinks pages). */
export function getDefaultMenuPageImages(cat) {
	if (!cat) return null;
	return firstMatch(DRINK_MENU_PAGE_RULES, nameBlob(cat), 'images');
}

/**
 * Single source of truth for which image a category card shows.
 *
 * The built-in default intentionally wins over a stored imageUrl unless the admin has
 * explicitly turned it off (useDefaultImage === false). Every category imported into
 * Firestore already carries an old imageUrl, so flipping this order would silently
 * change every card on the live site. Uploading or removing an image from the admin
 * panel sets useDefaultImage=false, which hands control back to the admin.
 *
 * `source` is 'custom' | 'default' | 'placeholder' so the admin UI can label it.
 */
export function resolveCategoryImage(cat, parentCat) {
	const defaultSrc = getDefaultCategoryImage(cat, parentCat);
	if (defaultSrc && cat?.useDefaultImage !== false) {
		return { src: defaultSrc, source: 'default', defaultSrc };
	}

	const custom = toText(cat?.imageUrl).trim();
	if (custom && !isPlaceholderUrl(custom)) {
		return { src: custom, source: 'custom', defaultSrc };
	}

	return { src: PLACEHOLDER_IMAGE, source: 'placeholder', defaultSrc };
}

/** Same idea as resolveCategoryImage but for the full-page menu images. */
export function resolveMenuPageImages(cat, inheritedImages) {
	const configured = Array.isArray(cat?.menuPageImages) ? cat.menuPageImages.filter(Boolean) : [];
	if (configured.length > 0) return { images: configured, source: 'custom' };

	const inherited = Array.isArray(inheritedImages) ? inheritedImages.filter(Boolean) : [];
	if (inherited.length > 0) return { images: inherited, source: 'inherited' };

	const defaults = getDefaultMenuPageImages(cat);
	if (defaults && defaults.length > 0 && cat?.useDefaultMenuPageImages !== false) {
		return { images: defaults, source: 'default' };
	}

	return { images: [], source: 'none' };
}
